import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { finalize, Observable } from 'rxjs';

import { AumicoCoreProvider } from '../../../../aumico-lib-core-fe/src/lib';
import { UsersConfig, UsersConfigService } from '../../../../aumico-lib-users-fe/src/lib/users.config';
import { RoutesUtil } from '../../../../aumico-lib-users-fe/src/lib/util/RoutesUtil';
import { CreateTaskRequestDto } from '../dtos/create-task-dto.interface';
import { GetProjectsTasksDto } from '../dtos/getProjectTasksDto.interface';
import { GetTasksOfProjectDto } from '../dtos/getTasksOfProjectDto';
import { UpdateTaskDto } from '../dtos/update-task-dto.interface';
import { TaskMetaInformation } from '../interfaces/task-meta-information.interface';
import { TasksFilter } from '../interfaces/tasks-filter.interface';

import { SubjectType } from '../enums/subject-type.enum';
@Injectable({
    providedIn: 'root',
})
export class TasksService {
    constructor(
        private http: HttpClient,
        @Inject(UsersConfigService) config: UsersConfig,
        private readonly coreProvider: AumicoCoreProvider,
        private routesUtil: RoutesUtil,
    ) { }

    getUserProjectsTasks(filter?: TasksFilter): Observable<GetProjectsTasksDto> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Tasks] Get User Projects Tasks');

        const params = this.routesUtil.buildHttpParams(filter);

        const url = this.routesUtil.getUserProjectsTasks();

        return this.http
            .get<GetProjectsTasksDto>(url, { params })
            .pipe(
                finalize(() => this.coreProvider.dispatchBlockingRequestEnd('[Aumico-Tasks] Get User Projects Tasks')),
            );
    }

    getProjectsTasks(projectId: string, filter?: TasksFilter, subjectType?: SubjectType): Observable<GetTasksOfProjectDto> {
        this.coreProvider.dispatchBlockingRequest('[Aumico-Tasks] Get User Projects Tasks');

        const params = this.routesUtil.buildHttpParams(filter);

        const url = this.routesUtil.getProjectsTasks(projectId, subjectType === SubjectType.PERSON);

        return this.http
            .get<GetTasksOfProjectDto>(url, { params })
            .pipe(
                finalize(() => this.coreProvider.dispatchBlockingRequestEnd('[Aumico-Tasks] Get User Projects Tasks')),
            );
    }

    createTask(
        projectId: string,
        createTaskRequestDto: CreateTaskRequestDto,
        subjectType?: SubjectType,
    ): Observable<TaskMetaInformation> {
        const url = this.routesUtil.createTask(projectId, subjectType === SubjectType.PERSON);
        return this.http.post<TaskMetaInformation>(url, createTaskRequestDto).pipe();
    }

    updateTask(taskId: string, updateTaskDto?: UpdateTaskDto): Observable<TaskMetaInformation> {
        const url = this.routesUtil.updateTask(taskId);
        return this.http.put<TaskMetaInformation>(url, updateTaskDto).pipe();
    }

    bulkUpdateTasks(taskIds: string[], updateTaskDto: UpdateTaskDto): Observable<TaskMetaInformation[]> {
        const url = this.routesUtil.bulkUpdateTasks();
        return this.http.put<TaskMetaInformation[]>(url, { taskIds, updateTaskDto }).pipe();
    }

    bulkDeleteTasks(taskIds: string[]): Observable<void> {
        const url = this.routesUtil.bulkDeleteTasks();
        return this.http.delete<void>(url, { body: { taskIds } }).pipe();
    }

    updateAllTasksStatusForProjectBySectionId(
        projectId: string,
        sectionId: string,
        updateTaskDto: UpdateTaskDto,
    ): Observable<TaskMetaInformation[]> {
        const url = this.routesUtil.updateAllTasksStatusForProjectBySectionId(projectId, sectionId);
        return this.http.put<TaskMetaInformation[]>(url, updateTaskDto).pipe();
    }

    deleteTask(taskId: string): Observable<void> {
        const url = this.routesUtil.deleteTask(taskId);
        return this.http.delete<void>(url).pipe();
    }
}
